import type { Board, BoardStepSection } from './boardModel';

/**
 * Where a task sits on the board: the phase and step it rests on, and the step
 * section itself.
 *
 * The step is handed back rather than copied so the caller derives status from
 * the very object the board gave the card. Two surfaces describing one task
 * must not be able to disagree about which step it is on.
 */
export interface TaskLocation {
  phaseLabel: string;
  stepLabel: string;
  step: BoardStepSection;
}

/**
 * Find the phase and step holding a task, or `undefined` if no step does.
 *
 * `undefined` is a real answer, not a defensive one: a task whose stored
 * position names a step the workflow no longer declares is bucketed into
 * Backlog as an orphan, and callers have to say something honest about a task
 * the board cannot place.
 *
 * A linear walk rather than a prepared index. A board holds one workflow's
 * columns and a person opens one task at a time; building a lookup map in
 * `buildBoard` would widen its output for a single consumer that asks once.
 */
export function locateTask(
  board: Board,
  taskId: string,
): TaskLocation | undefined {
  // Checked before the walk, because the walk would otherwise succeed. An
  // orphan is *bucketed into* Backlog so it stays visible rather than
  // vanishing, which puts it in a real step's `tasks` and makes it findable —
  // and the phase and step that walk returns are Backlog's, not the task's.
  // Reporting them would be the invented placement this function exists to
  // avoid.
  if (board.orphanTaskIds.has(taskId)) return undefined;

  for (const column of board.columns) {
    for (const step of column.steps) {
      if (step.tasks.some((task) => task.id === taskId)) {
        return {
          phaseLabel: column.label,
          stepLabel: step.label,
          step,
        };
      }
    }
  }
  return undefined;
}

/**
 * Every step on the board, by id.
 *
 * A transition names its target by id, and a button reading `plan-it` is an
 * identifier where a step name belongs. Built from the board rather than from
 * the workflow because the board is what the drawer already holds, and the two
 * must not be able to disagree about what a step is called.
 */
export function stepLabelsOf(board: Board): Map<string, string> {
  const labels = new Map<string, string>();
  for (const column of board.columns) {
    for (const step of column.steps) labels.set(step.stepId, step.label);
  }
  return labels;
}

/**
 * True when naming the phase would only repeat the step's name.
 *
 * The board's terminal columns are the usual case: the phase that holds `$done`
 * is itself called "Done", so "Done · Done" said one place twice. Same rule the
 * board applies to its own headers (`BoardPhaseGroup`): a phase named
 * differently from its step ("Approve" inside "Validation") still tells the
 * reader something, and both names stay. Case and surrounding space do not make
 * two names different; two empty names are not "the same name".
 */
export function phaseRepeatsStep(
  location: Pick<TaskLocation, 'phaseLabel' | 'stepLabel'>,
): boolean {
  const phase = location.phaseLabel.trim().toLowerCase();
  const step = location.stepLabel.trim().toLowerCase();
  return phase !== '' && phase === step;
}
